import {
  UploadCloud,
  ScanSearch,
  ClipboardCheck,
  Landmark,
  MessageSquareText,
  BarChart3,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
} from "lucide-react";

const INVOICE_STEPS = [
  {
    icon: UploadCloud,
    title: "Upload the invoice",
    desc: "Go to Invoice Capture and drag in (or click to browse) a GST tax invoice as a PDF, PNG or JPG.",
  },
  {
    icon: ScanSearch,
    title: "Gemini reads it live",
    desc: "The file is sent to Google Gemini in real time, which extracts invoice number, vendor, GSTIN, PO reference, line items and all amounts.",
  },
  {
    icon: ClipboardCheck,
    title: "Review & fix flags",
    desc: "The original document appears on the left; the extracted fields appear as an editable form on the right, with a validation check for GST arithmetic, PO balance and GSTIN format shown inline.",
  },
  {
    icon: Landmark,
    title: "Approve & book",
    desc: "Pick the booking department (Civil / Mechanical / Electrical), then click 'Approve & book' — or 'Submit anyway' if there is an amber warning you've reviewed.",
  },
  {
    icon: CheckCircle2,
    title: "Check the audit log",
    desc: "Every booking (clean or flagged) is written to the Audit Log table at the bottom of the page, with timestamp, amount and who booked it.",
  },
];

const INSIGHTS_STEPS = [
  {
    icon: MessageSquareText,
    title: "Pick a department",
    desc: "Go to Ask the Data and choose Inspection Management, HRMS or Procurement from the dropdown.",
  },
  {
    icon: Sparkles,
    title: "Ask a question",
    desc: "Click one of the 3 suggested questions, or type your own in plain English, then click Ask.",
  },
  {
    icon: BarChart3,
    title: "Read the insight",
    desc: "You'll get a short written answer, KPI cards, a bar chart, and a 'How this was derived' panel showing the sample SQL query behind the answer.",
  },
];

const FLAG_LEGEND = [
  { icon: CheckCircle2, label: "Green — Passed", desc: "The check is clear. No action needed.", className: "text-[#0E6E52] bg-[#E4F1EB] border-[#0E6E52]/20" },
  { icon: AlertTriangle, label: "Amber — Warning", desc: "Needs a look before booking (e.g. exceeds PO balance). You can still submit anyway.", className: "text-[#B7791A] bg-[#B7791A]/10 border-[#B7791A]/20" },
  { icon: XCircle, label: "Red — Error", desc: "Must be fixed in the form before you can book (e.g. GST arithmetic mismatch or invalid GSTIN).", className: "text-[#B23A3A] bg-[#B23A3A]/10 border-[#B23A3A]/20" },
];

const StepList = ({ steps, testPrefix }) => (
  <div className="space-y-4">
    {steps.map((s, i) => (
      <div key={i} className="flex gap-4" data-testid={`${testPrefix}-step-${i + 1}`}>
        <div className="shrink-0 flex flex-col items-center">
          <div className="w-9 h-9 rounded-full bg-[#E4F1EB] border border-[#0E6E52]/20 flex items-center justify-center">
            <s.icon className="w-4 h-4 text-[#0E6E52]" />
          </div>
          {i < steps.length - 1 && <div className="w-px flex-1 bg-[#DCE3DF] mt-1" />}
        </div>
        <div className="pb-4">
          <p className="text-xs font-mono uppercase tracking-[0.08em] text-[#5A6B63]">Step {i + 1}</p>
          <h4 className="text-base font-display font-medium text-[#141E1A] mt-0.5">{s.title}</h4>
          <p className="text-sm text-[#5A6B63] leading-relaxed mt-1">{s.desc}</p>
        </div>
      </div>
    ))}
  </div>
);

export default function UserGuide() {
  return (
    <main className="max-w-4xl mx-auto py-12 px-6" data-testid="user-guide-page">
      <div className="mb-10">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#0E6E52] mb-3">Help &amp; Documentation</p>
        <h1 className="text-4xl lg:text-5xl font-display font-medium tracking-tight text-[#141E1A]">User Guide</h1>
        <p className="mt-4 text-base text-[#5A6B63] max-w-2xl leading-relaxed">
          How to use this KRCL Invoice &amp; Insights proof-of-concept, feature by feature.
        </p>
      </div>

      <div
        className="bg-[#E4F1EB] border border-[#0E6E52]/20 rounded-[10px] p-5 flex gap-3 mb-10"
        data-testid="user-guide-real-vs-simulated"
      >
        <Info className="w-5 h-5 text-[#0E6E52] shrink-0 mt-0.5" />
        <div className="text-sm text-[#141E1A] leading-relaxed">
          <p className="font-medium mb-1">What's real vs. what's simulated</p>
          <p>
            <span className="font-medium">Invoice Capture's AI extraction is real</span> — every upload is sent live to
            Google Gemini. Booking to ERP and the Audit Log are a <span className="font-medium">simulation</span> (no real
            ERP is connected). <span className="font-medium">Ask the Data is a clickable prototype</span> — its answers,
            KPIs and chart use fixed sample data so the screen design can be reviewed before a real query engine is built.
          </p>
        </div>
      </div>

      <section className="bg-white border border-[#DCE3DF] rounded-[10px] shadow-[0_4px_20px_rgba(20,30,26,0.06)] p-6 sm:p-8 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <UploadCloud className="w-5 h-5 text-[#0E6E52]" />
          <h2 className="text-2xl font-display font-medium text-[#141E1A]">Invoice Capture &amp; Auto-fill</h2>
        </div>
        <StepList steps={INVOICE_STEPS} testPrefix="invoice" />
      </section>

      <section className="bg-white border border-[#DCE3DF] rounded-[10px] shadow-[0_4px_20px_rgba(20,30,26,0.06)] p-6 sm:p-8 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <BarChart3 className="w-5 h-5 text-[#0E6E52]" />
          <h2 className="text-2xl font-display font-medium text-[#141E1A]">Ask the Data</h2>
        </div>
        <StepList steps={INSIGHTS_STEPS} testPrefix="insights" />
      </section>

      <section className="bg-white border border-[#DCE3DF] rounded-[10px] shadow-[0_4px_20px_rgba(20,30,26,0.06)] p-6 sm:p-8">
        <h2 className="text-2xl font-display font-medium text-[#141E1A] mb-1">Understanding validation flags</h2>
        <p className="text-sm text-[#5A6B63] mb-6">
          Every field the AI extracts is checked automatically. Flags appear on the Review &amp; Validate screen.
        </p>
        <div className="space-y-3">
          {FLAG_LEGEND.map((f, i) => (
            <div
              key={i}
              data-testid={`flag-legend-${f.label.split(" ")[0].toLowerCase()}`}
              className={`flex items-start gap-3 border rounded-md px-4 py-3 ${f.className}`}
            >
              <f.icon className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">{f.label}</p>
                <p className="text-xs opacity-90 mt-0.5">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
